import {
  IAttachment,
  IChecklistAnswerItem,
  IFormMaterial,
  IMeasure,
  IRichText,
  ISignature,
  ISiteLocation,
} from '~/client/graph/types.generated'
import QuestionnaireType from '~/client/src/shared/enums/QuestionnaireType'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import { IPermitFormField } from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitFieldsBase.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { getQuestionnaireMapByType } from '~/client/src/shared/utils/PermitInstructionsHelper'
import { DASH, EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

const COMMA_SEPARATOR = ', '

export const stringMapper = (value: string) => String(value)
export const fileMapper = (file: IAttachment) => file.fileName

export const userMapper = (
  userId: string,
  projectMembersStore: ProjectMembersStore,
) => projectMembersStore.getById(userId)?.fullName

export const locationMapper = (
  location: ISiteLocation,
  locationAttributesStore: LocationAttributesStore,
) => locationAttributesStore.getLocationBySiteLocationObj(location)?.name

export const materialMapper = (
  material: IFormMaterial,
  materialsStore: MaterialsStore,
) => {
  let message = DASH
  if (material.materialId) {
    message =
      materialsStore.getInstanceById(material.materialId)?.productName ||
      EMPTY_STRING
  }
  if (material.procurementId) {
    message += ` ${material.procurementId}`
  }
  return message
}

export const measureMapper = (measure: IMeasure) => {
  return [...new Set([measure.value || DASH, measure.units || DASH])].join(' ')
}

export const checklistMapper = (
  answerValue: IChecklistAnswerItem,
  field?: IPermitFormField,
) => {
  if (answerValue.checklistItemId !== undefined) {
    const checklistItem = field.checklistItems?.find(
      i => i.id === answerValue.checklistItemId,
    )
    let answerText = DASH
    if (answerValue.answer !== undefined) {
      answerText =
        getQuestionnaireMapByType(
          checklistItem?.questionnaireType as QuestionnaireType,
        )?.[String(answerValue.answer)] || DASH
    }
    return `${checklistItem?.text || DASH}: ${answerText}`
  }
  return DASH
}

export const richTextMapper = (richText: IRichText) => {
  let message = DASH
  if (richText.content) {
    const plain = String(richText.content).replace(/<[^>]*>/g, ' ')
    message = plain.replace(/\s+/g, ' ').trim()
  }
  if (richText.attachments?.length) {
    message += ` ${richText.attachments
      .map(a => a.fileName)
      .join(COMMA_SEPARATOR)}`
  }
  return message
}

export const companyMapper = (
  companyId: string,
  companiesStore: CompaniesStore,
) => companiesStore.getCompanyNameById(companyId)

export const additionalDateMapper = (
  date: number,
  projectDateStore: ProjectDateStore,
) => projectDateStore.getWeekdayMonthDayAndYearToDisplay(date)

export const signatureMapper = (
  signature: ISignature,
  projectMembersStore: ProjectMembersStore,
) => {
  if (!signature) return DASH

  const signerName =
    (signature.userId &&
      projectMembersStore.getById(signature.userId)?.fullName) ||
    EMPTY_STRING

  const typedName = signature.typedValue || EMPTY_STRING

  const who = signerName || typedName

  const when =
    signature.timestamp !== undefined && signature.timestamp !== null
      ? new Date(signature.timestamp).toLocaleString()
      : EMPTY_STRING

  if (who && when) return `${who} - ${when}`
  if (who) return who
  if (when) return when
  return DASH
}
